import React, { ReactNode, useState } from "react";
import styles from "./styles.module.scss";
import classnames from "classnames";
import { Input, Tooltip } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  database?: string;
  table_schema?: string;
  subComponent?: (close: () => void) => ReactNode;
}

interface MultiSelectProps {
  options: SelectOption[];
  onChange?: (value: string) => void;
  value: (value: string) => boolean;
  showDivider?: boolean;
  maxOptionLimit?: number;
}

const MultiSelect = ({
  options,
  onChange,
  value,
  showDivider = false,
  maxOptionLimit = 50,
}: MultiSelectProps) => {
  const displayedOptions = options
    .sort((a, b) => {
      if (typeof a.label === "string" && typeof b.label === "string") {
        return a.label.localeCompare(b.label);
      }
      if (typeof a.value === "string" && typeof b.value === "string") {
        return a.value.localeCompare(b.value);
      }
      return 0;
    })
    .slice(0, maxOptionLimit);

  const getTooltipContent = (item: SelectOption): ReactNode => {
    const database = item.database;
    const schema = item.table_schema;
    let tooltipContent: ReactNode = item.label;
    if (database && schema) {
      tooltipContent = (
        <Stack direction={"column"} gap={"sm"}>
          <span>Database: {database}</span>
          <span>Schema: {schema}</span>
        </Stack>
      );
    } else if (database) {
      tooltipContent = (
        <Stack direction={"column"} gap={"sm"}>
          <span>Database: {database}</span>
        </Stack>
      );
    } else if (schema) {
      tooltipContent = (
        <Stack direction={"column"} gap={"sm"}>
          <span>Schema: {schema}</span>
        </Stack>
      );
    }
    return tooltipContent;
  };

  return (
    <ul className={styles.list}>
      {displayedOptions.map(item => {
        return (
          <li
            key={item.value}
            role="option"
            aria-selected={value(item.value)}
            tabIndex={0}
            className={classnames("al-flex al-items-center al-overflow-hidden al-min-w-0", {
              [styles.list_divider]: showDivider,
            })}
            onClick={e => {
              e.stopPropagation();
              onChange?.(item.value);
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onChange?.(item.value);
              }
            }}
          >
            <Input type="checkbox" checked={value(item.value)} className="me-2" readOnly />
            <div className="al-flex-1 al-min-w-0 al-overflow-hidden">
              <Tooltip title={getTooltipContent(item)}>
                <span className="al-truncate al-block al-w-full">{item.label}</span>
              </Tooltip>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

interface PhaseSelectProps {
  options: SelectOption[];
  onChange?: (value: string) => void;
  value?: string;
  showDivider?: boolean;
  optionStyles?: React.CSSProperties;
  handleOptionHover: (e: React.MouseEvent<HTMLLIElement>) => void;
  handleOptionClick: (e: React.MouseEvent<HTMLLIElement>) => void;
  openMultiselectOnHover?: boolean;
  openMultiselectOnFirstHover?: boolean;
}

const PhaseSelect = ({
  options,
  onChange,
  value,
  showDivider = false,
  optionStyles = {},
  handleOptionHover,
  handleOptionClick,
  openMultiselectOnHover = true,
  openMultiselectOnFirstHover = false,
}: PhaseSelectProps) => {
  const [firstHoverApplied, setFirstHoverApplied] = useState(false);
  return (
    <ul className={styles.list}>
      {options.map(item => (
        <li
          key={item.value}
          id={item.value}
          role="option"
          aria-selected={value === item.value}
          tabIndex={0}
          className={classnames({
            [styles.disabled]: item.disabled,
            [styles.active]: value === item.value,
            [styles.list_divider]: showDivider,
          })}
          onClick={e => {
            if (!openMultiselectOnHover && firstHoverApplied) {
              handleOptionClick(e);
            }
            if (!item.disabled) {
              e.stopPropagation();
              onChange?.(item.value);
            }
          }}
          onMouseEnter={e => {
            if (!openMultiselectOnHover && openMultiselectOnFirstHover && !firstHoverApplied) {
              setFirstHoverApplied(true);
              handleOptionHover(e);
            }
          }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              if (!item.disabled) {
                e.stopPropagation();
                onChange?.(item.value);
              }
            }
          }}
          style={optionStyles}
        >
          {item.label}
        </li>
      ))}
    </ul>
  );
};

export { MultiSelect, PhaseSelect };
